import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, ContextualMenu } from 'sonata-react-components';

import { PromptModal } from '@/components/common/PromptModal';

import type { AdInterface } from 'sonata-static-storage/lib/schemas/ad/ad.schema';

type Props = {
  ads: AdInterface[];
  className?: string;
  onDeleteSelected: () => void;
  onEditSelected: () => void;
  onHideSelected: () => void;
};

export function BulkActions({
  ads,
  className = '',
  onDeleteSelected,
  onEditSelected,
  onHideSelected
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [status, setStatus] = useState<'closed' | 'open'>('closed');
  const [showDeletionPrompt, setShowDeletionPrompt] = useState<boolean>(false);

  const handleDeleteAllClick = () => {
    setShowDeletionPrompt(true);
  };

  const handleCancelDeletion = () => {
    setShowDeletionPrompt(false);
  };

  const handleConfirmDeletion = () => {
    setShowDeletionPrompt(false);
    onDeleteSelected();
    setStatus('closed');
  };

  const handleEditAllClick = () => {
    onEditSelected();
    setStatus('closed');
  };

  const handleHideAllClick = () => {
    onHideSelected();
    setStatus('closed');
  };

  const enableBulkEdit =
    ads.every(ad => ad?.adOptions?.redirect?.destinationUrls?.length === 0) ||
    ads.every(ad => ad?.adOptions?.redirect?.destinationUrls?.length !== 0);

  const toggleVisibilityStatusLabel = ads.every(ad => ad.visible)
    ? formatMessage({
        defaultMessage: 'Hide all',
        id: 'creatives.adCreatives.bulkActions.popover.options.hideAll'
      })
    : ads.every(ad => !ad.visible)
      ? formatMessage({
          defaultMessage: 'Show all',
          id: 'creatives.adCreatives.bulkActions.popover.options.showAll'
        })
      : formatMessage({
          defaultMessage: 'Switch all views',
          id: 'creatives.adCreatives.bulkActions.popover.options.switchAllViews'
        });

  return (
    <>
      <ContextualMenu id={'bulkActions-menu'} placement="bottom">
        <ContextualMenu.Trigger>
          <ButtonUI
            className={className}
            disabled={ads.length <= 1}
            icon="fa-solid fa-list-ul"
            onClick={() => setStatus(status === 'open' ? 'closed' : 'open')}
            shape="squareRounded"
            text={formatMessage({
              defaultMessage: 'Bulk actions',
              id: 'creatives.adCreatives.bulkActions.button'
            })}
            type={status === 'open' ? 'secondary' : 'primary'}
          />
        </ContextualMenu.Trigger>
        <ContextualMenu.Item icon="fa-regular fa-eye" onClick={handleHideAllClick} text={toggleVisibilityStatusLabel} />
        <ContextualMenu.Item
          icon="fa-solid fa-trash"
          onClick={handleDeleteAllClick}
          text={formatMessage({
            defaultMessage: 'Delete all',
            id: 'creatives.adCreatives.bulkActions.popover.options.deleteAll'
          })}
        />
        <ContextualMenu.Item
          disabled={!enableBulkEdit}
          icon="fa-solid fa-pen-to-square"
          onClick={handleEditAllClick}
          text={formatMessage({
            defaultMessage: 'Edit all',
            id: 'creatives.adCreatives.bulkActions.popover.options.editAll'
          })}
        />
      </ContextualMenu>
      {showDeletionPrompt && (
        <PromptModal
          cancelCallback={handleCancelDeletion}
          confirmCallback={handleConfirmDeletion}
          texts={{
            buttons: {
              cancel: formatMessage({
                defaultMessage: 'Cancel',
                id: 'common.cancel'
              }),
              confirm: formatMessage({
                defaultMessage: 'Confirm',
                id: 'common.confirm'
              })
            },
            description: formatMessage({
              defaultMessage: "Deleting the selected ads can't be undone.",
              id: 'creatives.adCreatives.bulkActions.prompts.deletion.description'
            }),
            title: formatMessage({
              defaultMessage: 'Are you sure you want to delete the selected ads?',
              id: 'creatives.adCreatives.bulkActions.prompts.deletion.title'
            })
          }}
        />
      )}
    </>
  );
}
